import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from '../api';

export default function SalesReport() {
  const [from, setFrom] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'));
  const [sales, setSales] = useState([]);
  const [settings, setSettings] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const pageCount = Math.ceil(sales.length / rowsPerPage);
  const paginatedSales = sales.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const gotoPage = (num) => {
    if (num < 1 || num > pageCount) return;
    setCurrentPage(num);
  };

  useEffect(() => {
    fetchReport();
    api.get('/settings/get').then(res => setSettings(res.data || {}));
  }, [from, to]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sales, from, to]);

  const fetchReport = async () => {
    const res = await api.get('/sales-reports', { params: { from, to } });
    setSales(res.data.sales || []);
  };

  // GSTR-1 JSON Export Mapping
  const toGSTR1JSON = (salesRows, yourGSTIN, periodMMYYYY, stateCode = "29") => {
    const b2b = [];
    const b2cs = [];
    for (const row of salesRows) {
      if (row.CustomerGSTIN) {
        let b2bEntry = b2b.find(e => e.ctin === row.CustomerGSTIN);
        if (!b2bEntry) {
          b2bEntry = { ctin: row.CustomerGSTIN, inv: [] };
          b2b.push(b2bEntry);
        }
        b2bEntry.inv.push({
          inum: row.InvoiceNo,
          idt: dayjs(row.InvoiceDate).format('DD-MM-YYYY'),
          val: Number(row.Total || 0),
          pos: stateCode,
          rchrg: "N",
          inv_typ: "R",
          itms: [
            {
              num: 1,
              itm_det: {
                rt: row.TotalGST && row.Taxable ? (100 * Number(row.TotalGST) / Number(row.Taxable)) : 0,
                txval: Number(row.Taxable || 0),
                camt: Number(row.TotalCGST || 0),
                samt: Number(row.TotalSGST || 0),
                iamt: 0
              }
            }
          ]
        });
      } else {
        b2cs.push({
          sply_ty: "INTRAB2C",
          rt: row.TotalGST && row.Taxable ? (100 * Number(row.TotalGST) / Number(row.Taxable)) : 0,
          typ: "OE",
          pos: stateCode,
          txval: Number(row.Taxable || 0),
          iamt: 0,
          camt: Number(row.TotalCGST || 0),
          samt: Number(row.TotalSGST || 0)
        });
      }
    }
    return {
      gstin: yourGSTIN,
      fp: dayjs(from).format('MMYYYY'),
      version: "GSTJ1_V1.0",
      b2b,
      b2cs
    };
  };

  // Download as JSON
  const handleDownloadJSON = () => {
    const gstr1Json = toGSTR1JSON(sales, settings.GSTIN || "YOURGSTIN", dayjs(from).format('MMYYYY'));
    const blob = new Blob([JSON.stringify(gstr1Json, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `GSTR1_Sales_${from}_to_${to}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // PDF Export
  const exportPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(settings.BusinessName || 'Business Name', 105, 16, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(settings.Address || '', 105, 23, { align: 'center' });
    if (settings.GSTIN) doc.text(`GSTIN: ${settings.GSTIN}`, 105, 29, { align: 'center' });
    if (settings.Phone) doc.text(`Contact: ${settings.Phone}`, 105, 35, { align: 'center' });
    doc.setFontSize(13);
    doc.setTextColor(22, 88, 189);
    doc.text('Sales Report', 105, 46, { align: 'center' });
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(10);
    doc.text(`Period: ${dayjs(from).format('DD/MM/YYYY')} to ${dayjs(to).format('DD/MM/YYYY')}`, 105, 53, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);

    autoTable(doc, {
      startY: 60,
      head: [[
        'Invoice No', 'Date', 'Customer', 'GSTIN', 'Taxable Amt', 'CGST', 'SGST', 'Total GST', 'Total'
      ]],
      body: sales.map(b => [
        b.InvoiceNo,
        dayjs(b.InvoiceDate).format('DD/MM/YYYY'),
        b.CustomerName,
        b.CustomerGSTIN || '',
        Number(b.Taxable || 0).toFixed(2),
        Number(b.TotalCGST || 0).toFixed(2),
        Number(b.TotalSGST || 0).toFixed(2),
        Number(b.TotalGST || 0).toFixed(2),
        Number(b.Total || 0).toFixed(2)
      ]),
      theme: 'grid',
      styles: { font: 'helvetica', fontSize: 10, cellPadding: 2.2, halign: 'center' },
      headStyles: { fillColor: [129, 164, 253] }
    });

    doc.save(`SalesReport_${from}_to_${to}.pdf`);
  };

  // Excel Export
  const exportExcel = () => {
    const header = [
      [settings.BusinessName || 'Business Name'],
      [settings.Address || ''],
      [`GSTIN: ${settings.GSTIN || ''}`],
      [`Contact: ${settings.Phone || ''}`],
      ['Sales Report'],
      [`Period: ${dayjs(from).format('DD/MM/YYYY')} to ${dayjs(to).format('DD/MM/YYYY')}`],
      [],
      ['Invoice No', 'Date', 'Customer', 'GSTIN', 'Taxable Amt', 'CGST', 'SGST', 'Total GST', 'Total']
    ];
    const rows = sales.map(b => [
      b.InvoiceNo,
      dayjs(b.InvoiceDate).format('DD/MM/YYYY'),
      b.CustomerName,
      b.CustomerGSTIN || '',
      Number(b.Taxable || 0).toFixed(2),
      Number(b.TotalCGST || 0).toFixed(2),
      Number(b.TotalSGST || 0).toFixed(2),
      Number(b.TotalGST || 0).toFixed(2),
      Number(b.Total || 0).toFixed(2)
    ]);
    const data = [...header, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SalesReport');
    XLSX.writeFile(wb, `SalesReport_${from}_to_${to}.xlsx`);
  };

  // Email Report (calls backend API)
  const sendEmailReport = async () => {
    setSending(true);
    try {
      const gstr1Json = toGSTR1JSON(sales, settings.GSTIN || "YOURGSTIN", dayjs(from).format('MMYYYY'));
      await api.post('/send-sales-report', {
        email,
        from,
        to,
        sales,
        gstr1Json,
        company: settings
      });
      alert('Email sent!');
      setShowEmailModal(false);
    } catch (e) {
      alert('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Table columns
  const grandTotals = sales.reduce(
    (tot, s) => {
      tot.taxable += Number(s.Taxable || 0);
      tot.cgst += Number(s.TotalCGST || 0);
      tot.sgst += Number(s.TotalSGST || 0);
      tot.gst += Number(s.TotalGST || 0);
      tot.total += Number(s.Total || 0);
      return tot;
    },
    { taxable: 0, cgst: 0, sgst: 0, gst: 0, total: 0 }
  );

  return (
    <div>
      {/* Date Range Selector */}
      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <div>
          <label>From</label>
          <input type="date" className="form-control" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div>
          <label>To</label>
          <input type="date" className="form-control" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <button className="btn btn-outline-primary mt-3" onClick={() => {
          const now = dayjs();
          setFrom(now.format('YYYY-MM-DD'));
          setTo(now.format('YYYY-MM-DD'));
        }}>Today</button>
        <button className="btn btn-outline-primary mt-3" onClick={() => {
          const now = dayjs();
          setFrom(now.startOf('month').format('YYYY-MM-DD'));
          setTo(now.format('YYYY-MM-DD'));
        }}>This Month</button>
        <button className="btn btn-primary mt-3" onClick={fetchReport}>Get Report</button>
        {sales.length > 0 && (
          <>
            <button className="btn btn-success mt-3" onClick={exportPDF}>Download PDF</button>
            <button className="btn btn-info mt-3 text-white" onClick={exportExcel}>Export Excel</button>
            <button className="btn btn-warning mt-3" onClick={handleDownloadJSON}>Export GST JSON</button>
            <button className="btn btn-dark mt-3" onClick={() => setShowEmailModal(true)}>Send by Email</button>
          </>
        )}
      </div>

      {/* Grand Totals Cards */}
      <div className="row mb-3">
        <div className="col-md-3 mb-2">
          <div className="card border-primary shadow h-100">
            <div className="card-body">
              <div className="text-muted">Taxable Total</div>
              <div className="fs-4 fw-bold">₹{grandTotals.taxable.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div className="card border-success shadow h-100">
            <div className="card-body">
              <div className="text-muted">CGST Total</div>
              <div className="fs-4 fw-bold">₹{grandTotals.cgst.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div className="card border-info shadow h-100">
            <div className="card-body">
              <div className="text-muted">SGST Total</div>
              <div className="fs-4 fw-bold">₹{grandTotals.sgst.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div className="card border-dark shadow h-100">
            <div className="card-body">
              <div className="text-muted">Grand Total</div>
              <div className="fs-4 fw-bold">₹{grandTotals.total.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Report Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Invoice No</th>
              <th>Date</th>
              <th>Customer</th>
              <th>GSTIN</th>
              <th>Taxable Amt</th>
              <th>CGST</th>
              <th>SGST</th>
              <th>Total GST</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {paginatedSales.map((b, idx) => (
              <tr key={b.InvoiceNo}>
                <td>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                <td>{b.InvoiceNo}</td>
                <td>{dayjs(b.InvoiceDate).format('DD/MM/YYYY')}</td>
                <td>{b.CustomerName}</td>
                <td>{b.CustomerGSTIN || ''}</td>
                <td>₹{Number(b.Taxable || 0).toFixed(2)}</td>
                <td>₹{Number(b.TotalCGST || 0).toFixed(2)}</td>
                <td>₹{Number(b.TotalSGST || 0).toFixed(2)}</td>
                <td>₹{Number(b.TotalGST || 0).toFixed(2)}</td>
                <td>₹{Number(b.Total || 0).toFixed(2)}</td>
              </tr>
            ))}
            {!paginatedSales.length && (
              <tr>
                <td colSpan={10} className="text-center">No sales for selected period.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pageCount > 1 && (
        <nav className="mt-2">
          <ul className="pagination justify-content-end">
            <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
              <button className="page-link" onClick={() => gotoPage(1)}>First</button>
            </li>
            <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
              <button className="page-link" onClick={() => gotoPage(currentPage - 1)}>Prev</button>
            </li>
            {[...Array(pageCount)].map((_, i) => (
              <li key={i} className={`page-item${currentPage === i + 1 ? ' active' : ''}`}>
                <button className="page-link" onClick={() => gotoPage(i + 1)}>{i + 1}</button>
              </li>
            ))}
            <li className={`page-item${currentPage === pageCount ? ' disabled' : ''}`}>
              <button className="page-link" onClick={() => gotoPage(currentPage + 1)}>Next</button>
            </li>
            <li className={`page-item${currentPage === pageCount ? ' disabled' : ''}`}>
              <button className="page-link" onClick={() => gotoPage(pageCount)}>Last</button>
            </li>
          </ul>
        </nav>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <form
                onSubmit={e => {
                  e.preventDefault();
                  sendEmailReport();
                }}
              >
                <div className="modal-header">
                  <h5 className="modal-title">Send Sales Report by Email</h5>
                  <button type="button" className="btn-close" onClick={() => setShowEmailModal(false)}></button>
                </div>
                <div className="modal-body">
                  <label>Recipient Email:</label>
                  <input
                    type="email"
                    className="form-control"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                  <div className="form-text">
                    The report and GSTR-1 JSON will be sent as attachments.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowEmailModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-dark"
                    disabled={sending}
                  >
                    {sending ? "Sending..." : "Send"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
